import { defaultAxiosInstance } from '../config/axiosConfig';

/**
 * LLM Usage & Token Consumption Analytics.
 *
 * Company Admin (`/company-admin/usage`) and Platform Admin (`/platform/usage`)
 * expose the identical six endpoints with identical query params/response shapes
 * — this factory is parameterised by basePath so both screens share one client.
 * Auth is the caller's own x-api-key (defaultAxiosInstance attaches it already).
 */
class UsageAnalyticsService {
  constructor(basePath) {
    this.basePath = basePath;
    this.httpClient = defaultAxiosInstance;
    this._dimensions = null;
    this._dimensionsPromise = null;
  }

  /** KPI cards: totals + top 5 models + top 5 users/companies. */
  async getSummary(params = {}) {
    const res = await this.httpClient.get(`${this.basePath}/summary`, { params });
    return res.data;
  }

  /** The main grouped table. */
  async getBreakdown(params = {}) {
    const res = await this.httpClient.get(`${this.basePath}/breakdown`, { params });
    return res.data;
  }

  /** Chart data, optionally one series per user/model (split_by). */
  async getTimeseries(params = {}) {
    const res = await this.httpClient.get(`${this.basePath}/timeseries`, { params });
    return res.data;
  }

  /** Drill-down leaf: individual runs. */
  async getRuns(params = {}) {
    const res = await this.httpClient.get(`${this.basePath}/runs`, { params });
    return res.data;
  }

  /**
   * Full detail (trace) of one run — the leaf below the runs list.
   *
   * Text panes (input/output, tool args/result, step output) are stripped by the
   * server unless the caller holds `usage_run_content`, which a company admin
   * does NOT get automatically; the response says so via `content_redacted`, so
   * the UI gates the text off that flag rather than re-deriving it client-side.
   * Platform admins hold the wildcard and always see content; on /platform an
   * optional `company_id` scopes the lookup.
   */
  async getRunDetail(runId, params = {}) {
    const res = await this.httpClient.get(
      `${this.basePath}/runs/${encodeURIComponent(runId)}`,
      { params }
    );
    return res.data;
  }

  /**
   * Filter catalog — dimensions/run_types/grains/order_by/statuses/default_tz.
   * Fetched once and cached; build filter UI from this instead of hardcoding lists.
   */
  async getDimensions() {
    if (this._dimensions) return this._dimensions;
    if (!this._dimensionsPromise) {
      this._dimensionsPromise = this.httpClient
        .get(`${this.basePath}/dimensions`)
        .then((res) => {
          this._dimensions = res.data;
          return this._dimensions;
        })
        .catch((err) => {
          this._dimensionsPromise = null;
          throw err;
        });
    }
    return this._dimensionsPromise;
  }

  /**
   * Download a breakdown/runs export as a file.
   *
   * The endpoint is authenticated by the x-api-key header, so — same reasoning as
   * batchJobsService.downloadResults — a plain <a href>/window.location can't be
   * used. Uses the shared axios instance (interceptor attaches the header) with
   * responseType 'text' rather than 'json': axios's default response transform
   * only JSON.parses when responseType is 'json' (or unset), so 'text' hands back
   * the exact bytes the server sent — needed because a truncated CSV export ends
   * in a literal "# truncated at N rows" line we must detect and surface.
   */
  async exportData({ mode = 'breakdown', format = 'csv', ...params } = {}) {
    const res = await this.httpClient.get(`${this.basePath}/export`, {
      params: { mode, format, ...params },
      responseType: 'text',
      timeout: 300000, // large exports stream slowly; allow up to 5 minutes
    });

    const text = typeof res.data === 'string' ? res.data : JSON.stringify(res.data);
    let truncated = false;
    if (format === 'json') {
      try {
        truncated = Boolean(JSON.parse(text)?.truncated);
      } catch {
        truncated = false;
      }
    } else {
      truncated = /^#\s*truncated at \d+ rows/m.test(text);
    }

    const mime = format === 'json' ? 'application/json' : 'text/csv';
    const blob = new Blob([text], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `llm-usage-${mode}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { truncated };
  }
}

export function createUsageAnalyticsService(basePath) {
  return new UsageAnalyticsService(basePath);
}

export const companyUsageAnalyticsService = createUsageAnalyticsService('/company-admin/usage');
export const platformUsageAnalyticsService = createUsageAnalyticsService('/platform/usage');

/**
 * Platform-only maintenance action (re-pricing history). Deliberately not part of
 * the parameterised client above — it exists only under /platform, guarded
 * server-side by platform_llm_catalog (re-pricing is a catalog act, not a usage
 * read), and is written to the admin audit log.
 */
export async function repriceUsage(payload) {
  const res = await defaultAxiosInstance.post('/platform/usage/reprice', payload);
  return res.data;
}

const usageAnalyticsService = {
  createUsageAnalyticsService,
  companyUsageAnalyticsService,
  platformUsageAnalyticsService,
  repriceUsage,
};

export default usageAnalyticsService;
